import { api } from './client';

/**
 * Navigator API client for Guidance Suite.
 */
export const navigatorApi = {
  /**
   * Get Navigator configuration for a story.
   * @param {string} storyId - Story UUID
   * @returns {Promise<NavigatorConfig|null>} Navigator config or null if not configured
   */
  get: (storyId) => api.get(`/stories/${storyId}/navigator`),

  /**
   * Set Navigator configuration for a story.
   * @param {string} storyId - Story UUID
   * @param {Object} config - Navigator configuration
   * @param {string} config.primary_key - Primary Navigator key
   * @param {string[]} [config.secondary_keys] - Optional secondary Navigator keys (max 2)
   * @param {string} [config.story_flavor] - Optional story-specific flavor text
   * @returns {Promise<NavigatorConfig>} Updated Navigator config
   */
  set: (storyId, config) => api.put(`/stories/${storyId}/navigator`, config),

  /**
   * Classify a description into Navigator configuration.
   * @param {string} storyId - Story UUID
   * @param {string} description - Story description to classify
   * @returns {Promise<ClassifyResponse>} Classification result
   */
  classify: (storyId, description) =>
    api.post(`/stories/${storyId}/navigator/classify`, { description }),

  /**
   * List available Navigators for a unit.
   * @param {string} [unit='fiction'] - Unit to get Navigators for
   * @returns {Promise<Navigator[]>} List of available Navigators
   */
  listAvailable: (unit = 'fiction') => api.get(`/navigators/available?unit=${unit}`)
};

/**
 * @typedef {Object} NavigatorConfig
 * @property {string} primary_key - Primary Navigator key
 * @property {string[]} secondary_keys - Secondary Navigator keys (max 2)
 * @property {string|null} story_flavor - Story-specific flavor text
 * @property {string|null} configured_at - ISO timestamp when configured
 */

/**
 * @typedef {Object} Navigator
 * @property {string} key - Navigator key (e.g., 'mystery', 'romance')
 * @property {string} name - Display name (e.g., 'Mystery', 'Romance')
 * @property {string} tagline - Brief description
 * @property {string} icon - Emoji icon
 */

/**
 * @typedef {Object} ClassifyResponse
 * @property {string} primary - Recommended primary Navigator key
 * @property {string} primary_rationale - Explanation for recommendation
 * @property {string[]} secondary - Recommended secondary Navigator keys
 * @property {string|null} secondary_rationale - Explanation for secondary recommendations
 * @property {string} story_flavor - Extracted story elements
 */
